import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';

// Contextos
import { AuthProvider } from './contexts/AuthContext';
import { useThemeStore, applyThemeToDocument } from './store/useThemeStore';

// Componentes críticos (se cargan inmediatamente)
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import VerifyEmail from './components/VerifyEmail';
import OnboardingPage from './components/OnboardingPage';
import DashboardLayout from './components/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import RestaurantRedirect from './components/RestaurantRedirect';
import LandingPublicPreview from './components/LandingPublicPreview';
import AcceptInvitation from './components/AcceptInvitation';
import PageLoader from './components/ui/PageLoader';
import SuperAdminRestaurantSelector from './components/SuperAdminRestaurantSelector';
import OrgLocationSelector from './components/OrgLocationSelector';
import OrgTeam from './components/OrgTeam';
import OrgBranding from './components/OrgBranding';
import OrgMenu from './components/OrgMenu';
import OrgRecipes from './components/OrgRecipes';
import OrgAcceptInvitation from './components/OrgAcceptInvitation';
import RestaurantSuspended from './components/RestaurantSuspended';

// Componentes con Lazy Loading (se cargan bajo demanda)
const Dashboard = lazy(() => import('./components/Dashboard'));
const MenuPerformance = lazy(() => import('./components/MenuPerformance'));
// const SessionValidation = lazy(() => import('./components/SessionValidation'));
const CustomerExperience = lazy(() => import('./components/CustomerExperience'));
const StaffAmplifier = lazy(() => import('./components/StaffAmplifier'));
const StaffProfileRoute = lazy(() => import('./components/StaffProfilePage').then(m => ({ default: m.StaffProfileRoute })));
const KitchenMatrix = lazy(() => import('./components/KitchenMatrix'));
const MixologyLab = lazy(() => import('./components/MixologyLab'));
const Clients = lazy(() => import('./components/Clients'));
const Reviews = lazy(() => import('./components/Reviews'));
const Rewards = lazy(() => import('./components/Rewards'));
// const AIAgents = lazy(() => import('./components/AIAgents'));
// const AIMarketingCenter = lazy(() => import('./components/AIMarketingCenter'));
const FacturacionMain = lazy(() => import('./components/facturacion/FacturacionMain'));
// const ROIViewer = lazy(() => import('./components/ROIViewer'));
const Collaborators = lazy(() => import('./components/Collaborators'));
const RestaurantConfig = lazy(() => import('./components/RestaurantConfig'));
const Integrations = lazy(() => import('./components/Integrations'));
const TableManagement = lazy(() => import('./components/TableManagement'));
const Reservations = lazy(() => import('./components/Reservations'));
const Marketing = lazy(() => import('./components/Marketing'));
// const ReservationsManager = lazy(() => import('./components/ReservationsManager'));
// const LandingExperience = lazy(() => import('./components/LandingExperience'));
const AccountManagement = lazy(() => import('./components/AccountManagement'));
const Inventory = lazy(() => import('./components/inventory/Inventory'));
const TransferPrintable = lazy(() => import('./components/inventory/TransferPrintable'));

// Componente de rutas internas
const AppRoutes = () => {
  return (
    <Routes>
      {/* Rutas públicas */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
      <Route path="/accept-org-invitation/:token" element={<OrgAcceptInvitation />} />

      {/* Ruta de verificación de email */}
      <Route
        path="/verify-email"
        element={
          <ProtectedRoute requiresOnboardingComplete={false}>
            <VerifyEmail />
          </ProtectedRoute>
        }
      />

      {/* Ruta de onboarding - solo para owners sin onboarding completo */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute
            requiresRole="owner"
            requiresOnboardingComplete={false}
          >
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Ruta raíz - redirección inteligente */}
      <Route path="/" element={<RestaurantRedirect />} />

      {/* Ruta pública del landing - sin protección */}
      <Route
        path="/:restaurantId/landing-public"
        element={<LandingPublicPreview />}
      />

      {/* Página de suspensión - accesible sin protección para usuarios suspendidos */}
      <Route path="/suspended" element={<RestaurantSuspended />} />

      {/* Ruta de selección de restaurante para Super Admin */}
      <Route
        path="/admin/select-restaurant"
        element={
          <ProtectedRoute requiresRole="superAdmin" requiresOnboardingComplete={false}>
            <SuperAdminRestaurantSelector />
          </ProtectedRoute>
        }
      />

      {/* Selector de sedes para dueño/gerente de cadena (Sucursales) */}
      <Route
        path="/chain"
        element={
          <ProtectedRoute requiresOnboardingComplete={false}>
            <OrgLocationSelector />
          </ProtectedRoute>
        }
      />

      {/* Equipo de cadena: gestión de gerentes (solo dueño de cadena) */}
      <Route
        path="/chain/team"
        element={
          <ProtectedRoute requiresOnboardingComplete={false}>
            <OrgTeam />
          </ProtectedRoute>
        }
      />

      {/* Branding de marca: catálogo compartido (solo dueño de cadena) */}
      <Route
        path="/chain/branding"
        element={
          <ProtectedRoute requiresOnboardingComplete={false}>
            <OrgBranding />
          </ProtectedRoute>
        }
      />

      {/* Menú de marca: catálogo compartido (solo dueño de cadena) */}
      <Route
        path="/chain/menu"
        element={
          <ProtectedRoute requiresOnboardingComplete={false}>
            <OrgMenu />
          </ProtectedRoute>
        }
      />

      {/* Recetas de marca: catálogo compartido (solo dueño de cadena) */}
      <Route
        path="/chain/recipes"
        element={
          <ProtectedRoute requiresOnboardingComplete={false}>
            <OrgRecipes />
          </ProtectedRoute>
        }
      />

      {/* Rutas legacy /dashboard - redirigir a la raíz */}
      <Route
        path="/dashboard"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/dashboard/:restaurantId"
        element={<Navigate to="/:restaurantId" replace />}
      />

      {/* Ruta principal del dashboard - Solo restaurantId sin /dashboard */}
      <Route
        path="/:restaurantId"
        element={
          <ProtectedRoute requiredPermission="dashboard">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <Dashboard />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Vista ampliada del rendimiento del menú (acceso desde el bloque 4 del dashboard) */}
      <Route
        path="/:restaurantId/menu-performance"
        element={
          <ProtectedRoute requiredPermission="dashboard">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <MenuPerformance />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

{/* Ruta de Validación de Sesiones - OCULTA TEMPORALMENTE
      <Route
        path="/:restaurantId/session-validation"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <SessionValidation />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      */}

      <Route
        path="/:restaurantId/customer-experience"
        element={
          <ProtectedRoute requiredPermission="customerExperience">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <CustomerExperience />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/staff-amplifier"
        element={
          <ProtectedRoute requiredPermission="staffAmplifier">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <StaffAmplifier />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Fase 3 — Perfil del empleado como ruta dedicada (ocupa el mismo shell del Dashboard) */}
      <Route
        path="/:restaurantId/staff-amplifier/staff/:staffId"
        element={
          <ProtectedRoute requiredPermission="staffAmplifier">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <StaffProfileRoute />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/kitchen-matrix"
        element={
          <ProtectedRoute requiredPermission="kitchenMatrix">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <KitchenMatrix />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/mixology-lab"
        element={
          <ProtectedRoute requiredPermission="mixologyLab">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <MixologyLab />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/clients"
        element={
          <ProtectedRoute requiredPermission="clients">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <Clients />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/reviews"
        element={
          <ProtectedRoute requiredPermission="reviews">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <Reviews />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/rewards"
        element={
          <ProtectedRoute requiredPermission="rewards">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <Rewards />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/reservations"
        element={
          <ProtectedRoute requiredPermission="reservations">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <Reservations />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/inventory"
        element={
          <ProtectedRoute requiredPermission="inventoryManagement">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <Inventory />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Vista imprimible de la guía de traslado — standalone, sin DashboardLayout. */}
      <Route
        path="/:restaurantId/inventory/transfers/:transferId/print"
        element={
          <ProtectedRoute requiredPermission="inventoryManagement">
            <Suspense fallback={<PageLoader />}>
              <TransferPrintable />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/marketing"
        element={
          <ProtectedRoute requiredPermission="marketing">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <Marketing />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

{/* Ruta de AI Agents - OCULTA TEMPORALMENTE
      <Route
        path="/:restaurantId/ai-agents"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <AIAgents />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      */}

{/* Ruta de Marketing Center - OCULTA TEMPORALMENTE
      <Route
        path="/:restaurantId/marketing-center"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <AIMarketingCenter />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      */}

      <Route
        path="/:restaurantId/billing"
        element={
          <ProtectedRoute requiredPermission="billing">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <FacturacionMain />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

{/* Ruta de ROI Viewer - OCULTA TEMPORALMENTE
      <Route
        path="/:restaurantId/roi-viewer"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <ROIViewer />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      */}

      <Route
        path="/:restaurantId/collaborators"
        element={
          <ProtectedRoute requiredPermission="collaborators">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <Collaborators />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/restaurant-config"
        element={
          <ProtectedRoute requiredPermission="settings">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <RestaurantConfig />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/integrations"
        element={
          <ProtectedRoute requiredPermission="integrations">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <Integrations />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:restaurantId/table-management"
        element={
          <ProtectedRoute requiredPermission="tableManagement">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <TableManagement />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

{/* Ruta de Gestión de Reservas - OCULTA TEMPORALMENTE
      <Route
        path="/:restaurantId/reservations-manager"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <ReservationsManager />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      */}

{/* Ruta de Landing Experience - OCULTA TEMPORALMENTE
      <Route
        path="/:restaurantId/landing-experience"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <LandingExperience />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      */}

      <Route
        path="/:restaurantId/account-management"
        element={
          <ProtectedRoute requiredPermission="accountManagement">
            <DashboardLayout>
              <Suspense fallback={<PageLoader />}>
                <AccountManagement />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Rutas legacy sin restaurantId - redirigir a la raíz */}
      <Route
        path="/session-validation"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/customer-experience"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/consumer-experience"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/staff-amplifier"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/kitchen-matrix"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/mixology-lab"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/clients"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/reviews"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/rewards"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/ai-agents"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/marketing-center"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/billing"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/roi-viewer"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/settings"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/collaborators"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/restaurant-config"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/integrations"
        element={<Navigate to="/" replace />}
      />

      {/* Página de error no autorizado */}
      <Route
        path="/unauthorized"
        element={
          <div className="min-h-screen bg-surface-app flex items-center justify-center">
            <div className="text-center text-content-primary">
              <h1 className="text-4xl font-bold mb-4">🚫 Acceso Denegado</h1>
              <p className="text-content-muted mb-6">No tienes permisos para acceder a esta página.</p>
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="btn-kumia-primary"
              >
                Volver al Dashboard
              </button>
            </div>
          </div>
        }
      />
    </Routes>
  );
};

function App() {
  // Sincroniza el tema con el DOM en toda la app (incluido login) y ante cualquier cambio.
  const theme = useThemeStore((s) => s.theme);
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Toaster
            position="top-right"
            containerStyle={{ zIndex: 999999 }}
            toastOptions={{
              duration: 4000,
              style: {
                background: '#2d2d2d',
                color: '#ffffff',
                border: '1px solid #7AC943',
              },
            }}
          />

          <AppRoutes />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
